import { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) outputArray[i] = rawData.charCodeAt(i);
  return outputArray;
}

/**
 * Web Push subscription lifecycle for the current user.
 * Returns permission + subscribed state and enable/disable + test actions.
 */
export function usePushNotifications() {
  const [supported] = useState(
    typeof window !== "undefined" &&
      "serviceWorker" in navigator &&
      "PushManager" in window &&
      "Notification" in window
  );
  const [permission, setPermission] = useState(
    typeof Notification !== "undefined" ? Notification.permission : "default"
  );
  const [subscribed, setSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);

  const refreshStatus = useCallback(async () => {
    if (!supported) return;
    try {
      const reg = await navigator.serviceWorker.getRegistration();
      if (!reg) { setSubscribed(false); return; }
      const sub = await reg.pushManager.getSubscription();
      setSubscribed(!!sub);
      setPermission(Notification.permission);
    } catch (e) {
      setSubscribed(false);
    }
  }, [supported]);

  useEffect(() => { refreshStatus(); }, [refreshStatus]);

  const enable = useCallback(async () => {
    if (!supported) throw new Error("Push notifications not supported on this browser");
    setLoading(true);
    try {
      // 1. Get VAPID public key from backend
      const { data: keyData } = await api.get("/push/vapid-public-key");
      if (!keyData.enabled) throw new Error("Push notifications not enabled on server");

      // 2. Ensure service worker is ready
      const reg = await navigator.serviceWorker.ready;

      // 3. Ask for permission
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== "granted") throw new Error(perm === "denied" ? "Notification permission denied" : "Notification permission not granted");

      // 4. Subscribe (or reuse)
      let sub = await reg.pushManager.getSubscription();
      if (!sub) {
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(keyData.public_key),
        });
      }

      // 5. Send to backend
      const raw = sub.toJSON();
      await api.post("/push/subscribe", {
        endpoint: raw.endpoint,
        keys: { p256dh: raw.keys.p256dh, auth: raw.keys.auth },
        user_agent: navigator.userAgent,
      });
      setSubscribed(true);
      return true;
    } catch (e) {
      throw new Error(formatApiError(e) || e.message);
    } finally {
      setLoading(false);
    }
  }, [supported]);

  const disable = useCallback(async () => {
    if (!supported) return;
    setLoading(true);
    try {
      const reg = await navigator.serviceWorker.getRegistration();
      const sub = reg && (await reg.pushManager.getSubscription());
      if (sub) {
        const endpoint = sub.endpoint;
        try { await sub.unsubscribe(); } catch (e) { /* ignore */ }
        try { await api.post("/push/unsubscribe", { endpoint }); } catch (e) { /* ignore */ }
      }
      setSubscribed(false);
    } finally {
      setLoading(false);
    }
  }, [supported]);

  const sendTest = useCallback(async () => {
    const { data } = await api.post("/push/test");
    return data;
  }, []);

  return { supported, permission, subscribed, loading, enable, disable, sendTest, refreshStatus };
}
